import { differenceInMinutes } from "date-fns";
import { useCallback, useState } from 'react';

// Note: Although highly modified, the basic idea for this hook came from the following article:
// https://blog.wick.technology/pwa-install-prompt/

export const disabledStorageKey = "InstallPwaPromptDisabled";
export const lastShownTimestampStorageKey = "InstallPwaPromptLastShownTimestamp";
export const promptIntervalMinutesStorageKey = "InstallPwaPromptIntervalMinutes";

export function getInstallPwaPromptDisabled(): boolean {
    return (localStorage.getItem(disabledStorageKey) ?? "false") === "true";
}

export function setInstallPwaPromptDisabled(disabled: boolean) {
    localStorage.setItem(disabledStorageKey, disabled ? "true" : "false");
}

export function getInstallPwaPromptLastShownTimestamp(): Date {
    return new Date(localStorage.getItem(lastShownTimestampStorageKey) ?? 0);
}

export function setInstallPwaPromptLastShownTimestamp(timestamp: Date) {
    localStorage.setItem(lastShownTimestampStorageKey, timestamp.toISOString());
}

export function getInstallPwaPromptIntervalMinutes(defaultIntervalMinutes: number): number {
    const item = localStorage.getItem(promptIntervalMinutesStorageKey);
    return item ? Number(item) : defaultIntervalMinutes;
}

export function setInstallPwaPromptIntervalMinutes(intervalMinutes: number): void {
    localStorage.setItem(promptIntervalMinutesStorageKey, intervalMinutes.toString());
}

export function restoreInstallPwaPromptDefaults(){
    localStorage.removeItem(disabledStorageKey);
    localStorage.removeItem(lastShownTimestampStorageKey);
    localStorage.removeItem(promptIntervalMinutesStorageKey);
}

export function getShowInstallPwaPrompt(promptIntervalMinutes: number): boolean {
    const alreadyInstalled = navigator.standalone; // only valid for IOS
    const disabled = getInstallPwaPromptDisabled();
    const lastShownTimestamp = getInstallPwaPromptLastShownTimestamp();
    const minutesSinceLastShown = differenceInMinutes(new Date(), lastShownTimestamp);

    // console.log(`alreadyInstalled = ${alreadyInstalled}, disabled = ${disabled}, lastShownTimestamp = ${lastShownTimestamp}, minutesSinceLastShown = ${minutesSinceLastShown}`);

    return !alreadyInstalled &&
        !disabled &&
        (isNaN(minutesSinceLastShown) || (minutesSinceLastShown >= promptIntervalMinutes));
}

export default function useShouldShowInstallPwaPrompt(defaultPromptIntervalMinutes: number): [
    boolean,
    (timestamp: Date, disable: boolean) => void
] {
    const promptIntervalMinutes = getInstallPwaPromptIntervalMinutes(defaultPromptIntervalMinutes);

    const [showInstallPwaPrompt, setShowInstallPwaPrompt] = useState(
        getShowInstallPwaPrompt(promptIntervalMinutes)
    );

    const setInstallPwaPromptShown = useCallback((timestamp: Date, disable: boolean) => {
        setShowInstallPwaPrompt(false);
        setInstallPwaPromptLastShownTimestamp(timestamp);
        setInstallPwaPromptDisabled(disable);
    }, []);

    return [
        showInstallPwaPrompt,
        setInstallPwaPromptShown
    ];
}